import React from "react";

import {
  MessageCircle,
  PenTool,
  TrendingUp,
  ArrowRight,
} from "lucide-react";

import { WHATSAPP_URL } from "@/lib/brand";

const STEPS = [
  {
    icon: MessageCircle,

    step: "01",

    title: "Tell us about your business",

    body:
      "Message us on WhatsApp and share your business details, goals and services. Quick, simple and beginner-friendly.",
  },

  {
    icon: PenTool,

    step: "02",

    title: "We build your online presence",

    body:
      "Our team designs a modern website with SEO, WhatsApp integration and high-converting layouts tailored for your business.",
  },

  {
    icon: TrendingUp,

    step: "03",

    title: "Start getting more customers",

    body:
      "Receive calls, WhatsApp chats, bookings and customer inquiries through Google and your new online presence.",
  },
];

export default function HowItWorks() {
  return (
    <section
      id="how"
      data-testid="how-it-works-section"
      className="py-20 lg:py-28 bg-gradient-to-b from-slate-50 to-white"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">

        {/* Heading */}
        <div className="max-w-3xl mx-auto text-center">

          <p className="text-xs uppercase tracking-[0.22em] font-bold text-[var(--gf-blue)]">
            How It Works
          </p>

          <h2 className="font-display mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.08]">
            Launch your business online in{" "}
            <span className="text-[var(--gf-blue)]">
              3 simple steps.
            </span>
          </h2>

          <p className="mt-5 text-lg text-[var(--gf-text-2)] leading-relaxed">
            We make websites and digital growth simple for local businesses.
            No complicated process. No technical headache.
          </p>
        </div>

        {/* Steps */}
        <div className="relative mt-16 grid md:grid-cols-3 gap-7">

          {/* Dashed Line */}
          <div className="hidden md:block absolute top-14 left-[12%] right-[12%] h-px border-t-2 border-dashed border-[var(--gf-border)]" />

          {STEPS.map((s, i) => (
            <div
              key={i}
              data-testid={`how-step-${i}`}
              className="relative bg-white rounded-3xl border border-[var(--gf-border)] p-8 gf-shadow-soft hover:-translate-y-1 transition-all duration-300"
            >

              {/* Top */}
              <div className="flex items-start justify-between">

                <div className="h-14 w-14 rounded-2xl bg-[var(--gf-primary)] text-white flex items-center justify-center shadow-lg">
                  <s.icon className="h-6 w-6" />
                </div>

                <span className="font-display text-5xl font-extrabold text-[var(--gf-blue)]/10">
                  {s.step}
                </span>
              </div>

              {/* Content */}
              <h3 className="font-display mt-6 text-2xl font-extrabold leading-tight">
                {s.title}
              </h3>

              <p className="text-sm text-[var(--gf-text-2)] mt-3 leading-relaxed">
                {s.body}
              </p>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-14 flex flex-col items-center">

          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="how-it-works-cta"
            className="inline-flex items-center gap-2.5 bg-[var(--gf-green)] hover:bg-[var(--gf-green-deep)] text-white font-semibold h-14 px-8 rounded-full transition-all hover:-translate-y-0.5 shadow-lg"
          >
            <MessageCircle className="h-5 w-5" />

            Start on WhatsApp

            <ArrowRight className="h-4 w-4" />
          </a>

          <p className="mt-4 text-sm text-[var(--gf-text-2)]">
            Free consultation • Fast delivery • WhatsApp support
          </p>
        </div>
      </div>
    </section>
  );
}
